import React, { useEffect, useState } from "react";
import { X, Upload, Link2 } from "lucide-react";
import { api, fmtErr } from "../lib/api";
import { toast } from "sonner";

export default function MediaPicker({ open, onClose, onSelect }) {
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const [altInput, setAltInput] = useState("");

  const load = () => api.get("/admin/media").then(({ data }) => setMedia(data)).catch(() => {});

  useEffect(() => {
    if (open) load();
  }, [open]);

  if (!open) return null;

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("alt", file.name.replace(/\.[^.]+$/, "").replace(/[-_]/g, " "));
      const { data } = await api.post("/admin/media", fd);
      toast.success("Photo uploaded");
      onSelect({ url: data.url, alt: data.alt, focal: data.focal });
      onClose();
    } catch (err) {
      toast.error(fmtErr(err, "Upload failed"));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-[#061325]/70 backdrop-blur-sm flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Choose an image" data-testid="media-picker-modal">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h3 className="font-heading text-2xl font-semibold text-[#0B1D3A]">Choose an Image</h3>
          <button onClick={onClose} data-testid="media-picker-close" aria-label="Close" className="p-2 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 overflow-y-auto flex-1">
          <label className={`flex items-center justify-center gap-2 border-2 border-dashed border-amber-400/60 rounded-xl py-6 cursor-pointer text-[#0B1D3A] font-semibold hover:bg-amber-50 transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`} data-testid="media-picker-upload">
            <Upload className="w-5 h-5 text-amber-600" />
            {uploading ? "Uploading…" : "Upload from this device (JPG, PNG, WebP — max 5 MB)"}
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} />
          </label>

          <div className="flex gap-2 mt-4">
            <input value={urlInput} onChange={(e) => setUrlInput(e.target.value)} placeholder="…or paste an image URL" className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid="media-picker-url-input" />
            <input value={altInput} onChange={(e) => setAltInput(e.target.value)} placeholder="Alt text" className="w-40 rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid="media-picker-alt-input" />
            <button
              onClick={() => { if (urlInput.trim()) { onSelect({ url: urlInput.trim(), alt: altInput.trim(), focal: { x: 50, y: 50 } }); onClose(); } }}
              className="px-4 py-2 rounded-lg bg-[#0B1D3A] text-amber-200 text-sm font-semibold flex items-center gap-1.5"
              data-testid="media-picker-use-url"
            >
              <Link2 className="w-4 h-4" /> Use
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-6">
            {media.map((m) => (
              <button key={m.id} onClick={() => { onSelect({ url: m.url, alt: m.alt, focal: m.focal }); onClose(); }}
                data-testid={`media-picker-item-${m.id}`}
                className="group relative rounded-xl overflow-hidden border border-slate-200 hover:border-amber-500 transition-colors aspect-video">
                <img src={m.url} alt={m.alt || ""} className="w-full h-full object-cover" style={{ objectPosition: `${m.focal?.x ?? 50}% ${m.focal?.y ?? 50}%` }} />
                <span className="absolute inset-x-0 bottom-0 bg-[#061325]/70 text-amber-100 text-[11px] px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity truncate">{m.alt || m.filename}</span>
              </button>
            ))}
            {media.length === 0 && <p className="col-span-full text-center text-slate-400 text-sm py-8">No uploads yet — upload your first photo above.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
